import React, { useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { message } from 'antd';
import { createStyles } from 'antd-style';
import { ConnectionStatus } from './components/ConnectionStatus';
import { MessageList } from './components/MessageList';
import { QuickReply } from './components/QuickReply';
import { MessageInput } from './components/MessageInput';
import { ImagePreview } from './components/ImagePreview';
import { useSocket } from './hooks/useSocket';
import { useQrList, QrItem } from './hooks/useQrList';
import { useWindowVisibility } from './hooks/useWindowVisibility';
import { useImageUpload } from './hooks/useImageUpload';
import { useNotificationSound } from './hooks/useNotificationSound';

const useStyles = createStyles(() => ({
    chatContainer: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    debugInfo: {
        padding: '8px 16px',
        backgroundColor: '#fff7e6',
        borderLeft: '4px solid #faad14',
        fontSize: '12px',
        color: '#8c8c8c',
        borderBottom: '1px solid #f0f0f0',
    },
}));

interface ChatRoomProps {
    orderId: string;
    orderNo: string;
    token: string;
    onError?: (error: string) => void;
}

export interface ChatRoomRef {
    sendMessage: (content: string, messageType?: 'text' | 'image') => void;
}

export const ChatRoom = forwardRef<ChatRoomRef, ChatRoomProps>(({ orderId, orderNo, token, onError }, ref) => {
    const { styles } = useStyles();

    // 检测窗口可见性
    const { isWindowVisible, isTabActive } = useWindowVisibility();
    // 使用通知提示音hook
    const { playNotificationSound } = useNotificationSound();
    // 使用 useCallback 稳定 onError 函数引用
    const stableOnError = useCallback((error: string) => {
        onError?.(error);
    }, [onError]);

    // 使用自定义Hooks
    const { connected, messages, sendMessage, markAsRead } = useSocket({
        orderId,
        orderNo,
        token,
        onError: stableOnError,
        isWindowVisible, // 传递窗口可见性状态
        onNewMessage: playNotificationSound, // 传递通知提示音函数
    });

    // 暴露sendMessage方法给父组件
    useImperativeHandle(ref, () => ({
        sendMessage: (content: string, messageType?: 'text' | 'image') => {
            sendMessage(content, messageType);
        }
    }), [sendMessage]);

    const { qrList, qrLoading } = useQrList();

    // 图片上传功能
    const { selectedImage, imagePreview, uploadProgress, handleImageSelect } = useImageUpload({
        orderNo,
        isConnected: connected,
        onSendMessage: sendMessage,
    });

    // 当窗口重新变为可见时，标记未读消息为已读
    useEffect(() => {
        if (isWindowVisible && connected && messages.length > 0) {
            const unreadCustomerMessages = messages.filter(msg =>
                msg.senderRole === 'customer' &&
                msg.isRead === false &&
                msg.id
            );

            if (unreadCustomerMessages.length > 0) {
                console.log('客服端窗口重新可见，标记未读消息为已读');
                setTimeout(() => {
                    markAsRead();
                }, 500); // 短暂延迟模拟客服阅读
            }
        }
    }, [isWindowVisible, connected, messages, markAsRead]);

    // 处理消息发送
    const handleSendMessage = (content: string, messageType?: 'text' | 'image') => {
        sendMessage(content, messageType);
    };

    // 快捷回复二维码信息
    const handleQuickReply = (qr: QrItem) => {
        if (!connected) {
            message.error('连接已断开，无法发送消息');
            return;
        }

        const qrMessage = qr.receiver_name?.split('|') || [];
        for (const item of qrMessage) {
            sendMessage(item);
        }
    };

    return (
        <div className={styles.chatContainer}>
            {/* 消息列表 */}
            <MessageList messages={messages} currentUserRole="cp" />

            {/* 快捷回复区域 */}
            <QuickReply
                qrList={qrList}
                connected={connected}
                orderNo={orderNo}
                onQuickReply={handleQuickReply}
            />

            {/* 图片预览区域 */}
            <ImagePreview
                imagePreview={imagePreview}
                uploadProgress={uploadProgress}
                selectedImage={selectedImage}
            />

            {/* 输入区域 */}
            <MessageInput
                connected={connected}
                loading={false}
                qrListEmpty={qrList.length === 0}
                qrLoading={qrLoading}
                onSendMessage={handleSendMessage}
                onImageSelect={handleImageSelect}
            />
        </div>
    );
}); 